import { useRef, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Check, X, RotateCw, ImageOff, FileText, Copy } from "lucide-react";
import { PhotoSourceButtons } from "@/components/PhotoSourceButtons";

// ONE shared photo uploader used everywhere photos are added (donor page, parts, exit
// actions, and future callers). Guarantees the same behavior so the paths can never
// drift apart again: per-photo status (uploading -> ✓/✗) with one-tap retry, a running
// "X of Y uploaded" count, plain size/HEIC/network failure messages, and a 120s timeout
// + abort on every request. Nothing vanishes: results stay visible until dismissed.
// A build sheet is a PDF or a picture of one — or the Freightliner dealer sheet (Excel / CSV), which goes to the exact reader.
const DOC_ACCEPT = "application/pdf,.pdf,image/*,.heic,.heif,.doc,.docx,.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv";
const isSpreadsheet = (f) => /\.(xlsx|xls|csv)$/i.test(f?.name || "");
const UPLOAD_TIMEOUT_MS = 120000;
// Photos ALWAYS get both "Take photo" and "Choose from gallery" (PhotoSourceButtons). `cameraFirst` only picks the
// big red look; it used to switch the camera on, and the truck pages never had it set (lost 2026-07-06).

let _uid = 0;
const nextId = () => `pf_${Date.now()}_${_uid++}`;

function classifyError(err, item) {
  if (item?.clientError) return item.clientError;
  if (err?.code === "ERR_CANCELED") return "Canceled — tap retry.";
  const resp = err?.response;
  if (resp?.data?.detail) return formatApiErrorDetail(resp.data.detail);
  if (!resp) return "Network dropped or timed out — tap retry.";
  return "Upload failed — tap retry.";
}

export function PhotoUploader({
  endpoint,
  onUploaded,           // called after EACH successful upload (pass the saved photo)
  onAllComplete,        // called once the queue drains
  onFailed,             // called when the server REFUSES a file (it is logged with its reason)
  multiple = true,
  maxSizeMB = 25,
  buttonLabel = "Files",    // documents only — photo buttons always read "Take photo" / "Choose from gallery"
  fieldName = "file",
  disabled = false,
  variant = "photo",        // "photo" → image accept; "document" → files, no camera
  cameraFirst = false,      // big red primary look (parts flow); both sources are offered either way
  onFiles = null,           // staging mode: hand selected files to the parent instead of uploading here
  spreadsheetTo = null,     // documents only: an Excel/CSV file goes to this (the exact reader), not the endpoint
  testidPrefix = "photo-uploader",
  size = "sm",              // "sm" (h-7, default everywhere) | "md" (h-9, to align with primary action buttons)
  labelClass = "",          // e.g. "hidden xl:inline" in tight rows — the icons and titles stay
}) {
  const inputRef = useRef(null);           // document picker
  const controllers = useRef({});          // id -> AbortController
  const [items, setItems] = useState([]);  // {id, file, name, status: uploading|done|error, msg}
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);

  const isDoc = variant === "document";
  const hCls = size === "md" ? "h-9" : "h-7";

  const uploaded = items.filter((i) => i.status === "done").length;
  const skipped = items.filter((i) => i.status === "skipped").length;
  const failed = items.filter((i) => i.status === "error").length;
  const inFlight = items.filter((i) => i.status === "uploading").length;
  const total = items.length;

  const patch = (id, next) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...next } : i)));

  const uploadOne = useCallback(async (item) => {
    // client-side size guard -> plain message, no wasted request
    const mb = item.file.size / (1024 * 1024);
    if (mb > maxSizeMB) {
      patch(item.id, { status: "error", msg: `Too large (${mb.toFixed(1)} MB) — max ${maxSizeMB} MB.` });
      return;
    }
    patch(item.id, { status: "uploading", msg: "" });
    const ctrl = new AbortController();
    controllers.current[item.id] = ctrl;
    const fd = new FormData();
    fd.append(fieldName, item.file);
    try {
      const { data } = await api.post(endpoint, fd, { timeout: UPLOAD_TIMEOUT_MS, signal: ctrl.signal });
      if (data?.duplicate) {
        // content-hash duplicate — this exact photo is already on the record.
        // Skip it (never a second copy) and don't re-add it to the gallery.
        patch(item.id, { status: "skipped", msg: "already uploaded" });
      } else {
        patch(item.id, { status: "done", msg: "" });
        onUploaded?.(data?.photo || data);
      }
    } catch (e) {
      patch(item.id, { status: "error", msg: classifyError(e, item) });
      if (e.response) onFailed?.();
    } finally {
      delete controllers.current[item.id];
    }
  }, [endpoint, fieldName, maxSizeMB, onUploaded, onFailed]);

  // sequential (concurrency 1) — gentle on flaky yard wifi and preserves order
  const runQueue = useCallback(async (queue) => {
    for (const item of queue) {
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(item);
    }
    onAllComplete?.();
  }, [uploadOne, onAllComplete]);

  const addFiles = (fileList) => {
    let files = Array.from(fileList || []);
    if (!files.length) return;
    if (onFiles) { onFiles(files); return; }   // staging mode — parent collects files & uploads later
    if (spreadsheetTo) {
      files.filter(isSpreadsheet).forEach((f) => spreadsheetTo(f));
      files = files.filter((f) => !isSpreadsheet(f));
      if (!files.length) return;
    }
    const newItems = files.map((f) => ({ id: nextId(), file: f, name: f.name || "photo", status: "uploading", msg: "" }));
    setItems((prev) => [...prev, ...newItems]);
    runQueue(newItems);
  };

  const retryOne = (item) => runQueue([{ ...item, status: "uploading", msg: "", clientError: null }]);
  const retryAllFailed = () => {
    const toRetry = items.filter((i) => i.status === "error");
    setItems((prev) => prev.map((i) => (i.status === "error" ? { ...i, status: "uploading", msg: "" } : i)));
    runQueue(toRetry);
  };
  const dismiss = () => {
    Object.values(controllers.current).forEach((c) => c.abort());
    controllers.current = {};
    setItems([]);
  };

  const allDone = total > 0 && inFlight === 0;

  const onDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (disabled) return;
    const dropped = e.dataTransfer?.files;
    if (dropped?.length) addFiles(multiple ? dropped : [dropped[0]]);
  };

  return (
    <div
      className="inline-block"
      data-testid={`${testidPrefix}-root`}
      onDragEnter={(e) => { e.preventDefault(); if (disabled) return; dragDepth.current += 1; setDragging(true); }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={(e) => { e.preventDefault(); dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragging(false); }}
      onDrop={onDrop}
    >
      {isDoc ? (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={DOC_ACCEPT}
            multiple={multiple}
            className="hidden"
            data-testid={`${testidPrefix}-input`}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            data-testid={`${testidPrefix}-trigger`}
            className={`inline-flex items-center gap-1 ${hCls} px-2 border text-xs rounded-md disabled:opacity-50 transition-colors ${dragging ? "border-[#B01E28] bg-[#B01E28]/5 ring-2 ring-[#B01E28]/30" : "border-slate-200 hover:border-slate-400"}`}
            title={multiple ? "Upload files (or drag & drop)" : "Upload a file (or drag & drop)"}
          >
            <FileText className="w-3.5 h-3.5" />{dragging ? "Drop to upload" : buttonLabel}
          </button>
        </>
      ) : (
        <PhotoSourceButtons onFiles={addFiles} multiple={multiple} disabled={disabled} testidPrefix={testidPrefix}
          look={cameraFirst ? "primary" : size === "md" ? "md" : "compact"} labelClass={labelClass} dragging={dragging} />
      )}

      {total > 0 && (
        <div
          data-testid={`${testidPrefix}-panel`}
          className="mt-2 w-72 max-w-[80vw] rounded-lg border border-slate-200 bg-white shadow-sm p-2 text-xs"
        >
          <div className="flex items-center justify-between mb-1.5">
            <span
              data-testid={`${testidPrefix}-count`}
              className={`font-semibold ${failed ? "text-[#B01E28]" : allDone ? "text-emerald-600" : "text-slate-600"}`}
            >
              {allDone ? (
                <>
                  {uploaded} uploaded{!failed && !skipped ? " ✓" : ""}
                  {skipped ? ` · ${skipped} skipped (already there)` : ""}
                  {failed ? ` · ${failed} failed ✗` : ""}
                </>
              ) : (
                <>{uploaded + skipped} of {total} processed{skipped ? ` · ${skipped} skipped` : ""}</>
              )}
            </span>
            <button type="button" onClick={dismiss} data-testid={`${testidPrefix}-dismiss`}
              className="text-slate-400 hover:text-slate-700" title="Dismiss">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <ul className="space-y-1 max-h-44 overflow-auto">
            {items.map((i) => (
              <li key={i.id} data-testid={`${testidPrefix}-item`} className="flex items-center gap-1.5">
                {i.status === "uploading" && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 shrink-0" />}
                {i.status === "done" && <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                {i.status === "skipped" && <Copy className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                {i.status === "error" && <ImageOff className="w-3.5 h-3.5 text-[#B01E28] shrink-0" />}
                <span className="truncate flex-1 text-slate-600" title={i.name}>{i.name}</span>
                {i.status === "skipped" && <span className="text-[10px] text-amber-600 shrink-0">already there</span>}
                {i.status === "error" && (
                  <button type="button" onClick={() => retryOne(i)} data-testid={`${testidPrefix}-retry`}
                    className="inline-flex items-center gap-0.5 text-[#B01E28] hover:underline shrink-0">
                    <RotateCw className="w-3 h-3" />retry
                  </button>
                )}
              </li>
            ))}
          </ul>
          {items.some((i) => i.status === "error") && (
            <div className="mt-1.5 pt-1.5 border-t border-slate-100">
              <p className="text-[11px] text-[#B01E28] mb-1">
                {items.find((i) => i.status === "error")?.msg}
              </p>
              <button type="button" onClick={retryAllFailed} data-testid={`${testidPrefix}-retry-all`}
                className="inline-flex items-center gap-1 text-[#B01E28] font-medium hover:underline">
                <RotateCw className="w-3 h-3" />Retry all failed
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
